/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    TextField,
    MenuItem,
    ToggleButtonGroup,
    ToggleButton,
    Chip,
    Autocomplete,
    CircularProgress,
    Divider,
    Button,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useSelector } from '../../redux/store';
import { callApi } from '../../utils/apiCall';
import Iconify from '../Iconify';
import { DentallyAvailabilityDrawer } from './index';
import { fetchDentallyPractitioners, fetchDentallyTreatmentFee } from '../../utils/dentallyApi';
import { availPractitionerName, parseDentallyDate } from '../../utils/dentallyUtils';
import useDentallyReasonOptions from '../../hooks/useDentallyReasonOptions';
import { sectionLabelStyle, presetChipGroupStyle, presetChipStyle } from '../../styles/DentallyBookingStyle';

const DURATION_OPTIONS = [15, 30, 45, 60];

const METHOD_OPTIONS = [
    { value: 'IN_PRACTICE', label: 'In Practice', icon: 'mdi:hospital-building' },
    { value: 'VIDEO_CALL', label: 'Video', icon: 'mdi:video-outline' },
    { value: 'PHONE', label: 'Phone', icon: 'mdi:phone-outline' },
];

export default function DentallyBookingFormAppointment({ values, onChange, errors, openAvailabilityOnMount }) {
    const practiceId = useSelector((s) => s.practiceTreatmentList.practiceId);
    const { reasons: reasonOptions, reasonTreatmentMap } = useDentallyReasonOptions(practiceId);
    const allUsers = useSelector((s) => s.practiceUser.practiceUser.results || []);

    // Build assignable users with practice user IDs (not auth user IDs)
    // Filter out ADMIN/SUPPORT/superuser roles, same as FilterAssigneeList but keeping practice user id (el.id)
    const assignableUsers = allUsers
        .filter((el) => el?.is_enabled && el?.role !== 'ADMIN' && el?.role !== 'SUPPORT' && !el?.user?.is_superuser)
        .map((el) => ({
            id: el.id, // practice user ID — required by appointments API
            name: `${el?.user?.first_name || ''} ${el?.user?.last_name || ''}`.trim(),
            role: el.role,
            avatar: el?.user?.avatar,
            dentally_practitioner_id: el?.dentally_practitioner_id ?? null,
        }));

    const [treatments, setTreatments] = useState([]);
    const [loadingTreatments, setLoadingTreatments] = useState(false);
    const latestTreatmentRef = useRef(null);
    const [practitioners, setPractitioners] = useState([]);
    const [loadingPractitioners, setLoadingPractitioners] = useState(false);
    const [practitionerLoadError, setPractitionerLoadError] = useState(false);
    const [availabilityOpen, setAvailabilityOpen] = useState(openAvailabilityOnMount || false);
    const [manualMode, setManualMode] = useState(false);
    const [reasonAutoFilled, setReasonAutoFilled] = useState(false);

    useEffect(() => {
        if (!practiceId) return;
        setLoadingTreatments(true);
        callApi(
            `/api/v1/practices/${practiceId}/treatments/?fields={id,treatment_item{id,name},price,custom_price,dentally_treatment_id,dentally_appointment_reason}&enabled=true&ordering=treatment_item__name&limit=200`,
            'get'
        )
            .then((r) => {
                if (r.ok) setTreatments(r.data.results || []);
            })
            .catch(() => {})
            .finally(() => setLoadingTreatments(false));
    }, [practiceId]);

    useEffect(() => {
        if (!practiceId) return;
        setLoadingPractitioners(true);
        fetchDentallyPractitioners(practiceId)
            .then(setPractitioners)
            .catch(() => {
                setPractitioners([]);
                setPractitionerLoadError(true);
            })
            .finally(() => setLoadingPractitioners(false));
    }, [practiceId]);

    // Auto-populate CRM assignees when a Dentally practitioner is selected
    useEffect(() => {
        if (!values.practitionerId) return;
        const matched = assignableUsers.find(
            (u) =>
                u.dentally_practitioner_id !== null &&
                Number(u.dentally_practitioner_id) === Number(values.practitionerId)
        );
        if (matched) {
            onChange('assignees', [matched.id]);
        }
    }, [values.practitionerId]); // eslint-disable-line react-hooks/exhaustive-deps

    // When user selects a slot from availability drawer, auto-fill: dateTime, durationMinutes, practitionerId, reason
    const handleSlotSelect = (slot) => {
        onChange('dateTime', parseDentallyDate(slot.start_time) || new Date(slot.start_time));
        const startD = parseDentallyDate(slot.start_time) || new Date(slot.start_time);
        const endD = parseDentallyDate(slot.finish_time) || new Date(slot.finish_time);
        const duration = Math.round((endD - startD) / 60000);
        onChange('durationMinutes', DURATION_OPTIONS.includes(duration) ? duration : 30);
        if (slot.practitioner_id != null) {
            onChange('practitionerId', Number(slot.practitioner_id));
        }
        if (slot.reason) onChange('reason', slot.reason);
        if (slot.treatmentId) onChange('treatmentId', slot.treatmentId);
        if (slot.treatmentName) onChange('treatmentName', slot.treatmentName);
        // If drawer passed a linked treatment name from reason mapping, find and select it
        if (slot.reasonLinkedTreatment && !slot.treatmentId) {
            const match = treatments.find((t) => t.treatment_item?.name === slot.reasonLinkedTreatment);
            if (match) {
                onChange('treatmentId', match.id);
                onChange('treatmentName', match.treatment_item?.name || null);
                const crmPrice = match.custom_price || match.price || 0;
                if (crmPrice > 0) onChange('paymentAmount', crmPrice);
            }
        }
        setAvailabilityOpen(false);
    };

    const selectedPractitioner = practitioners.find((p) => Number(p.id) === Number(values.practitionerId)) || null;
    const selectedTreatment = treatments.find((t) => t.id === values.treatmentId) || null;

    // assignableUsers have shape { id, name, role, avatar } from FilterAssigneeList
    const selectedAssignees = assignableUsers.filter((u) =>
        (values.assignees || []).includes(u.id)
    );

    return (
        <Box sx={{ mb: 2 }}>
            <Typography sx={sectionLabelStyle}>APPOINTMENT</Typography>

            {/* Treatment */}
            <Autocomplete
                size="small"
                sx={{ mt: 1.5 }}
                options={treatments}
                loading={loadingTreatments}
                value={selectedTreatment}
                getOptionLabel={(opt) => opt.treatment_item?.name || ''}
                isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                onChange={(_, v) => {
                    const treatmentKey = v?.dentally_treatment_id || v?.id || null;
                    latestTreatmentRef.current = treatmentKey;
                    onChange('treatmentId', v?.id || null);
                    onChange('treatmentName', v?.treatment_item?.name || null);
                    const crmPrice = v?.custom_price || v?.price || 0;
                    if (crmPrice > 0) onChange('paymentAmount', crmPrice);
                    if (v?.dentally_appointment_reason) {
                        onChange('reason', v.dentally_appointment_reason);
                        setReasonAutoFilled(true);
                        setTimeout(() => setReasonAutoFilled(false), 2000);
                    } else {
                        onChange('reason', '');
                        setReasonAutoFilled(false);
                    }
                    if (v?.dentally_treatment_id) {
                        fetchDentallyTreatmentFee(practiceId, v.dentally_treatment_id)
                            .then((dp) => { if (dp > 0 && latestTreatmentRef.current === treatmentKey) onChange('paymentAmount', dp); })
                            .catch(() => {});
                    }
                }}
                renderOption={(props, opt) => (
                    <Box component="li" {...props} key={opt.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" noWrap>{opt.treatment_item?.name}</Typography>
                            </Box>
                            {opt.dentally_treatment_id && opt.dentally_treatment_id.trim() && (
                                <Chip size="small" label="Dentally" color="success" variant="outlined" sx={{ fontSize: 10, height: 20 }} />
                            )}
                            {opt.dentally_appointment_reason && opt.dentally_appointment_reason.trim() && (
                                <Chip size="small" label={opt.dentally_appointment_reason} color="info" variant="outlined" sx={{ fontSize: 10, height: 20, maxWidth: 120 }} />
                            )}
                        </Box>
                    </Box>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Treatment"
                        required
                        error={!!errors.treatmentId}
                        helperText={reasonAutoFilled ? 'Reason auto-filled from mapping' : errors.treatmentId}
                    />
                )}
            />

            {/* Reason */}
            <TextField
                select
                size="small"
                label={reasonAutoFilled ? 'Reason (auto-filled)' : 'Reason'}
                required
                sx={{
                    mt: 1.5, width: '100%',
                    ...(reasonAutoFilled && { '& fieldset': { borderColor: '#00AB55 !important' } }),
                }}
                value={values.reason || ''}
                onChange={(e) => { onChange('reason', e.target.value); setReasonAutoFilled(false); }}
                error={!!errors.reason}
                helperText={errors.reason}
            >
                {reasonOptions.map((r) => (
                    <MenuItem key={r} value={r} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flex: 1 }}>{r}</Box>
                        {reasonTreatmentMap[r] && (
                            <Chip size="small" label={`→ ${reasonTreatmentMap[r]}`} color="success" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
                        )}
                    </MenuItem>
                ))}
            </TextField>

            {/* Practitioner */}
            <Autocomplete
                size="small"
                sx={{ mt: 1.5 }}
                options={practitioners}
                value={selectedPractitioner}
                loading={loadingPractitioners}
                getOptionLabel={availPractitionerName}
                isOptionEqualToValue={(opt, val) => Number(opt.id) === Number(val?.id)}
                onChange={(_, v) => onChange('practitionerId', v?.id != null ? Number(v.id) : null)}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Practitioner"
                        required
                        error={!!errors.practitionerId}
                        helperText={errors.practitionerId}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loadingPractitioners && <CircularProgress size={16} />}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />
            {practitionerLoadError && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    Failed to load practitioners. Please close and reopen the form.
                </Typography>
            )}

            {/* Search availability button row */}
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'center' }}>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Iconify icon="mdi:magnify" />}
                    sx={{ whiteSpace: 'nowrap' }}
                    onClick={() => setAvailabilityOpen(true)}
                >
                    Search availability
                </Button>
                {!manualMode && (
                    <Button
                        variant="text"
                        size="small"
                        sx={{ whiteSpace: 'nowrap', color: 'text.secondary', textTransform: 'none', fontSize: 12 }}
                        onClick={() => setManualMode(true)}
                    >
                        Enter date/time manually
                    </Button>
                )}
            </Box>

            {/* Date & Time picker — shown when manualMode or a slot has been selected */}
            {(manualMode || values.dateTime) ? (
                <>
                    <Box sx={{ mt: 1.5 }}>
                        <DateTimePicker
                            label="Date & Time"
                            value={values.dateTime || null}
                            onChange={(v) => onChange('dateTime', v)}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    required: true,
                                    error: !!errors.dateTime,
                                    helperText: errors.dateTime,
                                    fullWidth: true,
                                },
                            }}
                        />
                    </Box>
                    {/* Duration chips */}
                    <Box sx={{ ...presetChipGroupStyle, mt: 1.5 }}>
                        {DURATION_OPTIONS.map((d) => (
                            <Chip
                                key={d}
                                label={`${d}m`}
                                size="small"
                                clickable
                                sx={presetChipStyle(values.durationMinutes === d)}
                                onClick={() => onChange('durationMinutes', d)}
                            />
                        ))}
                    </Box>
                    {errors.durationMinutes && (
                        <Typography variant="caption" color="error">
                            {errors.durationMinutes}
                        </Typography>
                    )}
                </>
            ) : (
                <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
                    <Typography variant="body2" color="text.secondary">
                        Use availability search above to select a slot, or{' '}
                        <Box
                            component="span"
                            onClick={() => setManualMode(true)}
                            sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline', fontSize: 'inherit' }}
                        >
                            enter manually
                        </Box>
                        .
                    </Typography>
                    {errors.dateTime && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                            {errors.dateTime}
                        </Typography>
                    )}
                </Box>
            )}

            {/* Method toggle */}
            <Box sx={{ mt: 1.5 }}>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5, display: 'block' }}
                >
                    Method
                </Typography>
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={values.method || 'IN_PRACTICE'}
                    onChange={(_, v) => v && onChange('method', v)}
                >
                    {METHOD_OPTIONS.map((opt) => (
                        <ToggleButton
                            key={opt.value}
                            value={opt.value}
                            sx={{ gap: 0.5, px: 1.5 }}
                        >
                            <Iconify icon={opt.icon} width={16} height={16} />
                            {opt.label}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Box>

            {/* Booked with — assignableUsers have { id, name, role, avatar } */}
            <Autocomplete
                multiple
                size="small"
                sx={{ mt: 1.5 }}
                options={assignableUsers}
                value={selectedAssignees}
                getOptionLabel={(u) => u.name || ''}
                isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                onChange={(_, v) => onChange('assignees', v.map((u) => u.id))}
                renderInput={(params) => <TextField {...params} label="Booked with" />}
            />

            {/* Notes */}
            <TextField
                size="small"
                label="Notes (optional)"
                multiline
                rows={2}
                sx={{ mt: 1.5, width: '100%' }}
                value={values.notes || ''}
                onChange={(e) => onChange('notes', e.target.value)}
            />

            <Divider sx={{ mt: 2 }} />

            {/* Nested availability drawer */}
            <DentallyAvailabilityDrawer
                open={availabilityOpen}
                onClose={() => setAvailabilityOpen(false)}
                practiceId={practiceId}
                onSelect={handleSlotSelect}
            />
        </Box>
    );
}

DentallyBookingFormAppointment.propTypes = {
    values: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    errors: PropTypes.object,
    openAvailabilityOnMount: PropTypes.bool,
};

DentallyBookingFormAppointment.defaultProps = {
    errors: {},
    openAvailabilityOnMount: false,
};
